import { FunctionComponent, memo, useMemo, useState } from "react";
import { IWritingIssuesCount, Permission, WritingIssueType } from "@edgetier/watchtower-types";
import { IProps } from "./writing-issues-frequency-table.types";
import {
    Box,
    BoxContent,
    BoxHeader,
    BoxHeading,
    Cell,
    Column,
    Heading,
    NumericValue,
    Table,
    TableColumns,
} from "@edgetier/client-components";
import useWritingIssuesCounts from "hooks-for/writing-issues/use-writing-issues-counts";
import WritingIssueTableMenu from "../writing-issue-table-menu";
import { useInteractionsMessageQueryParameters } from "hooks-for/interaction-messages/use-interactions-messages-query-parameters";
import { WRITING_ISSUES_NO_PREFIX_FILTERS } from "../writing-issues.constants";
import WritingIssuesFrequencyTableActionsMenu from "./writing-issues-frequency-table-actions-menu";
import VerifyPermission from "components-for/pages/verify-permission";
import "./writing-issues-frequency-table.scss";

/**
 * A table showing the frequency of particular writing issues.
 * @param props.onRowClick A function that will run when a row is cliekd.
 * @param props.parameters Optional parameters that will be passed in the request to get the table data.
 * @returns Table of writing issues showing their frequency.
 */
const WritingIssuesFrequencyTable: FunctionComponent<IProps> = ({ onRowClick, parameters = {} }) => {
    const interactionQueryParameters = useInteractionsMessageQueryParameters({
        additionalParameters: parameters,
        prefixValue: "interaction",
        excludedParameters: WRITING_ISSUES_NO_PREFIX_FILTERS,
    });
    const writingIssuesParameters = {
        ...interactionQueryParameters,
        groupBy: ["writing_issue_type", "issue"],
    };
    const { data: writingIssuesCounts } = useWritingIssuesCounts(writingIssuesParameters, {
        select: (items) => items.sort((one, two) => two.count - one.count),
    });

    const [issueTypeFilter, setIssueTypeFilter] = useState<WritingIssueType | undefined>();

    const filteredWritingIssuesCounts = useMemo(() => {
        if (typeof issueTypeFilter !== "undefined") {
            return writingIssuesCounts?.filter(
                (item) => item.groups[0].toLowerCase() === issueTypeFilter.toLowerCase()
            );
        }
        return writingIssuesCounts;
    }, [writingIssuesCounts, issueTypeFilter]);

    const [activeRowKey, setActiveRowKey] = useState<string | null>(null);

    /**
     * When a row is clicked, set that row as being active and call the onRowClick prop if it's defined.
     * @param writingIssueCount The writing issue count that was clicked on.
     */
    const onClickRow = (writingIssueCount: IWritingIssuesCount<[WritingIssueType, string]>) => {
        if (typeof onRowClick === "function") {
            onRowClick(writingIssueCount);
            setActiveRowKey((previousKey) =>
                previousKey === writingIssueCount.groups[1] ? null : writingIssueCount.groups[1]
            );
        }
    };

    return (
        <Box className="writing-issues-frequency-table">
            <BoxHeader>
                <BoxHeading>Issue Frequency</BoxHeading>
            </BoxHeader>
            <BoxContent horizontalPadding={false}>
                <div className="writing-issues-frequency-table__container">
                    <Table<IWritingIssuesCount<[WritingIssueType, string]>>
                        activeRowKey={activeRowKey}
                        getRowKey={(thing) => thing.groups[1]}
                        isInBox
                        onRowClick={onClickRow}
                        rows={filteredWritingIssuesCounts ?? null}
                        headerIsSticky
                    >
                        <TableColumns>
                            <Column>
                                <Heading sortProperty="groups[1]">Issue</Heading>
                                <Cell<IWritingIssuesCount<[WritingIssueType, string]>>>
                                    {({ groups }) => groups[1]}
                                </Cell>
                            </Column>

                            <Column>
                                <Heading
                                    isFiltered={typeof issueTypeFilter !== "undefined"}
                                    menu={
                                        <WritingIssueTableMenu
                                            value={issueTypeFilter}
                                            onClick={(value) =>
                                                setIssueTypeFilter((oldValue) =>
                                                    oldValue === value ? undefined : value
                                                )
                                            }
                                        />
                                    }
                                >
                                    Issue Type
                                </Heading>
                                <Cell<IWritingIssuesCount<[WritingIssueType, string]>>>
                                    {({ groups }) => groups[0]}
                                </Cell>
                            </Column>

                            <Column>
                                <Heading sortProperty="count">Frequency</Heading>
                                <Cell<IWritingIssuesCount<[WritingIssueType, string]>>>
                                    {({ count }) => <NumericValue value={count} />}
                                </Cell>
                            </Column>
                            <VerifyPermission permission={Permission.ManageWritingIssues}>
                                <Column>
                                    <Heading>&nbsp;</Heading>
                                    <Cell<IWritingIssuesCount<[WritingIssueType, string]>>>
                                        {({ groups: [writingIssueType, writingIssue] }) => (
                                            <>
                                                {writingIssueType.toString().toUpperCase() ===
                                                WritingIssueType.Spelling ? (
                                                    <div onClick={(event) => event.stopPropagation()}>
                                                        <WritingIssuesFrequencyTableActionsMenu
                                                            writingIssue={writingIssue}
                                                        />
                                                    </div>
                                                ) : (
                                                    <></>
                                                )}
                                            </>
                                        )}
                                    </Cell>
                                </Column>
                            </VerifyPermission>
                        </TableColumns>
                    </Table>
                </div>
            </BoxContent>
        </Box>
    );
};

export default memo(WritingIssuesFrequencyTable);
